import { useEffect, useState } from "react";
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function usePushNotifications() {
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const setupPushNotifications = async () => {
      try {
        // Temel kontroller
        if (!("serviceWorker" in navigator)) {
          throw new Error("Service Worker desteklenmiyor");
        }

        if (!("PushManager" in window)) {
          throw new Error("Push Manager desteklenmiyor");
        }

        // VitePWA service worker'ının kayıt olmasını bekle
        let registration = await navigator.serviceWorker.getRegistration();

        // Eğer yoksa, kayıt olmasını bekle
        if (!registration) {
          await new Promise((resolve) => {
            const checkForSW = async () => {
              const reg = await navigator.serviceWorker.getRegistration();
              if (reg) {
                registration = reg;
                resolve();
              } else {
                // 100ms sonra tekrar kontrol et
                setTimeout(checkForSW, 100);
              }
            };
            checkForSW();
          });
        }

        if (isCancelled) return;

        // Service Worker aktif olana kadar bekle
        if (registration.installing || registration.waiting) {
          await new Promise((resolve) => {
            const worker = registration.installing || registration.waiting;
            if (worker.state === "activated") {
              resolve();
            } else {
              worker.addEventListener("statechange", () => {
                if (worker.state === "activated") {
                  resolve();
                }
              });
            }
          });
        }

        // Notification izni al

        let permission = Notification.permission;

        if (permission === "default") {
          permission = await Notification.requestPermission();
        }

        if (permission !== "granted") {
          throw new Error(`Notification izni verilmedi: ${permission}`);
        }

        if (isCancelled) return;

        // VAPID public key (backend ile aynı)
        const publicKey =
          "BMRBPdEb9s0w9Z9zmezeOq-RBhQlXczBvLBTP99v6GDXVav6TNA-VYjQmihLVuuDT8yC8LXdi7fgcCcVgNXia6I";

        // Mevcut subscription kontrol et
        let existingSubscription =
          await registration.pushManager.getSubscription();

        if (existingSubscription) {
          // Endpoint kontrol et (geçerli mi?)
          if (existingSubscription.endpoint && existingSubscription.getKey) {
            setSubscription(existingSubscription);
            setError(null);
            setIsLoading(false);
            return;
          } else {
            await existingSubscription.unsubscribe();
          }
        }

        // Yeni subscription oluştur
        const newSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        if (!isCancelled) {
          setSubscription(newSubscription);
          setError(null);

          // Keys'leri base64 formatında logla
          const p256dh = newSubscription.getKey("p256dh");
          const auth = newSubscription.getKey("auth");

          // console.log("🔐 Keys hazır:", {
          //   p256dh: p256dh ? "OK" : "MISSING",
          //   auth: auth ? "OK" : "MISSING",
          // });

          // Test notification (sadece dev'de)
          if (import.meta.env.DEV) {
            setTimeout(() => {
              new Notification("Kids English Setup", {
                body: "Push notifications başarıyla kuruldu! 🎉",
                icon: "/pwa-192x192.png",
                tag: "setup-success",
              });
            }, 1000);
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("❌ Push notification kurulum hatası:", err);
          setError(err.message);
          setSubscription(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    // Sayfa yüklendikten sonra başlat
    const startSetup = () => {
      // Küçük bir delay ver ki VitePWA service worker kayıt olabilsin
      setTimeout(setupPushNotifications, 500);
    };

    if (document.readyState === "complete") {
      startSetup();
    } else {
      window.addEventListener("load", startSetup, { once: true });
    }

    return () => {
      isCancelled = true;
      window.removeEventListener("load", startSetup);
    };
  }, []);

  return { subscription, error, isLoading };
}

// Test notification gönder
export function sendTestNotification(title = "Test", body = "Test mesajı") {
  if ("Notification" in window && Notification.permission === "granted") {
    new Notification(title, {
      body,
      icon: "/pwa-192x192.png",
      tag: "manual-test",
    });
    return true;
  }
  return false;
}

// Subscription'ı JSON formatında al (backend için)
export function getSubscriptionJSON(subscription) {
  if (!subscription) return null;

  try {
    return subscription.toJSON();
  } catch (err) {
    console.error("Subscription JSON parse hatası:", err);
    return null;
  }
}
